import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility as ViewIcon, Person as PersonIcon } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { taskApi } from '../api/services';
import { useAuthStore } from '../store/authStore';
import { useCryptoKeyStore } from '../features/encryption';

const priorityColors: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

export function TaskListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { activeKey, gkdkSlots, loadGKDKSlots } = useCryptoKeyStore();
  const [gkdkLoadAttempted, setGkdkLoadAttempted] = useState(false);

  // Load GKDK slots on mount to get authority IDs
  useEffect(() => {
    if (activeKey && gkdkSlots.length === 0 && !gkdkLoadAttempted) {
      setGkdkLoadAttempted(true);
      loadGKDKSlots();
    }
  }, [activeKey, gkdkSlots.length, loadGKDKSlots, gkdkLoadAttempted]);

  // Get the first authority ID from GKDK slots (employee's authority)
  const authorityId = gkdkSlots.length > 0 ? gkdkSlots[0].authorityId : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks', authorityId],
    queryFn: () => taskApi.getMyTasks(authorityId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Fehler beim Laden der Aufgaben</Alert>;
  }

  const tasks = data?.tasks || [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Meine Aufgaben
      </Typography>

      {tasks.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              Keine offenen Aufgaben vorhanden
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aufgabe</TableCell>
                <TableCell>Prozess</TableCell>
                <TableCell>Prioritaet</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow
                  key={task.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                >
                  <TableCell>
                    <Typography variant="subtitle2">
                      {task.taskDisplay?.labels['de'] || task.name}
                    </Typography>
                    {task.taskDisplay?.descriptions['de'] && (
                      <Typography variant="caption" color="text.secondary">
                        {task.taskDisplay.descriptions['de'].substring(0, 60)}...
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {task.processDefinitionKey}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.taskDisplay?.priority || 'MEDIUM'}
                      size="small"
                      color={priorityColors[task.taskDisplay?.priority || 'MEDIUM']}
                    />
                  </TableCell>
                  <TableCell>
                    {task.createTime
                      ? new Date(task.createTime).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {task.assignee === user?.username ? (
                      <Chip
                        icon={<PersonIcon />}
                        label="Mir zugewiesen"
                        size="small"
                        color="success"
                      />
                    ) : task.assignee ? (
                      <Chip
                        label={`Zugewiesen: ${task.assignee}`}
                        size="small"
                        color="default"
                      />
                    ) : (
                      <Chip label="Offen" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Details anzeigen">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tasks/${task.id}`);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
